import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import api, { formatErr } from "../../../lib/api";
import { card, label } from "../roads/_roadsCommon";

/** Ch 26.3 — Three.js wireframe of the structural frame. */
export default function Structural3DPanel({ projectId }) {
  const [model, setModel] = useState(null);
  const [err, setErr] = useState("");
  const containerRef = useRef(null);
  const sceneRef = useRef(null);

  useEffect(() => { (async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/structural-re/model`);
      setModel(data.model);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  })(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!model || !containerRef.current) return;
    const cont = containerRef.current;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0a1220);
    const w = cont.clientWidth, h = cont.clientHeight;
    const camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 500);
    camera.position.set(20, 18, 20); camera.lookAt(0, 6, 0);
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(w, h);
    cont.innerHTML = ""; cont.appendChild(renderer.domElement);
    scene.add(new THREE.AmbientLight(0xffffff, 0.9));
    const dir = new THREE.DirectionalLight(0xffffff, 0.6);
    dir.position.set(15, 30, 10); scene.add(dir);
    const ctrl = new OrbitControls(camera, renderer.domElement);
    ctrl.target.set(8, 4, 4);

    const xMap = {}, yMap = {}, zMap = {};
    (model.grid.x_axes || []).forEach((a) => xMap[a.label] = a.x_m);
    (model.grid.y_axes || []).forEach((a) => yMap[a.label] = a.x_m);
    (model.grid.levels || []).forEach((a) => zMap[a.label] = a.z_m);

    // Grid floor
    const g = new THREE.GridHelper(40, 40, 0x444466, 0x222244);
    g.rotation.x = 0; scene.add(g);

    // Columns — gray boxes
    (model.columns || []).forEach((c) => {
      const x = xMap[c.axis_x] ?? 0, y = yMap[c.axis_y] ?? 0;
      const z0 = zMap[c.from_level] ?? 0, z1 = zMap[c.to_level] ?? 3;
      const b = (c.b_mm || 400) / 1000, d = (c.d_mm || 400) / 1000;
      const geom = new THREE.BoxGeometry(b, z1 - z0, d);
      const mat = new THREE.MeshStandardMaterial({ color: 0x9ca3af });
      const mesh = new THREE.Mesh(geom, mat);
      mesh.position.set(x, (z0 + z1) / 2, y);
      scene.add(mesh);
    });
    // Beams — orange boxes
    (model.beams || []).forEach((bm) => {
      const x1 = xMap[bm.from_axis_x] ?? 0, y1 = yMap[bm.from_axis_y] ?? 0;
      const x2 = xMap[bm.to_axis_x]   ?? 0, y2 = yMap[bm.to_axis_y]   ?? 0;
      const z = zMap[bm.level] ?? 3;
      const len = Math.hypot(x2 - x1, y2 - y1) || 1;
      const b = (bm.b_mm || 300) / 1000, d = (bm.d_mm || 500) / 1000;
      const geom = new THREE.BoxGeometry(len, d, b);
      const mat = new THREE.MeshStandardMaterial({ color: 0xF26B1F });
      const mesh = new THREE.Mesh(geom, mat);
      mesh.position.set((x1 + x2) / 2, z - d / 2, (y1 + y2) / 2);
      mesh.rotation.y = -Math.atan2(y2 - y1, x2 - x1);
      scene.add(mesh);
    });
    // Slabs — translucent panels
    (model.slabs || []).forEach((s) => {
      const corners = (s.corner_axes || []).map(([ax, ay]) =>
        [xMap[ax] ?? 0, yMap[ay] ?? 0]);
      if (corners.length !== 4) return;
      const z = zMap[s.level] ?? 3;
      const xMin = Math.min(...corners.map((c) => c[0])), xMax = Math.max(...corners.map((c) => c[0]));
      const yMin = Math.min(...corners.map((c) => c[1])), yMax = Math.max(...corners.map((c) => c[1]));
      const geom = new THREE.BoxGeometry(xMax - xMin, (s.th_mm || 150) / 1000, yMax - yMin);
      const mat = new THREE.MeshStandardMaterial({ color: 0x3aa0ff,
                                                          opacity: 0.4, transparent: true });
      const mesh = new THREE.Mesh(geom, mat);
      mesh.position.set((xMin + xMax) / 2, z, (yMin + yMax) / 2);
      scene.add(mesh);
    });
    // Footings — green boxes below GF
    (model.footings || []).forEach((f) => {
      const c = (model.columns || []).find((cc) => cc.id === f.column_id);
      if (!c) return;
      const x = xMap[c.axis_x] ?? 0, y = yMap[c.axis_y] ?? 0;
      const z = zMap[c.from_level] ?? 0;
      const geom = new THREE.BoxGeometry(f.B_m || 2, f.depth_m || 1.5, f.L_m || 2);
      const mat = new THREE.MeshStandardMaterial({ color: 0x22c55e });
      const mesh = new THREE.Mesh(geom, mat);
      mesh.position.set(x, z - (f.depth_m || 1.5) / 2, y);
      scene.add(mesh);
    });

    let stop = false;
    const loop = () => {
      if (stop) return;
      ctrl.update(); renderer.render(scene, camera);
      requestAnimationFrame(loop);
    };
    loop();
    sceneRef.current = { stop: () => { stop = true; renderer.dispose(); } };
    return () => sceneRef.current?.stop();
  }, [model]);

  return (
    <div data-testid="structural-3d" style={{ display: "grid",
            gridTemplateColumns: "1fr 280px", gap: 8, height: "calc(100vh - 280px)", minHeight: 700 }}>
      <div style={{ ...card, overflow: "hidden", padding: 0 }}>
        <div ref={containerRef} data-testid="structural-3d-canvas"
              style={{ width: "100%", height: "100%" }} />
      </div>
      <div style={{ ...card, padding: 8 }}>
        <div style={label}>Element legend</div>
        <Legend colour="#9ca3af" name="Columns" />
        <Legend colour="#F26B1F" name="Beams" />
        <Legend colour="#3aa0ff" name="Slabs (40 % opacity)" />
        <Legend colour="#22c55e" name="Footings" />
        <p style={{ fontSize: 10, color: "#aaa", marginTop: 12, lineHeight: 1.6 }}>
          Drag to rotate · scroll to zoom · right-click drag to pan.
          The frame is rendered to scale from your grid coordinates.
        </p>
        {err && <div style={{ marginTop: 8, color: "#FF8888", fontSize: 11 }}>{err}</div>}
      </div>
    </div>
  );
}

function Legend({ colour, name }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
      <span style={{ width: 16, height: 8, background: colour, borderRadius: 2 }} />
      <span style={{ fontSize: 11 }}>{name}</span>
    </div>
  );
}
